import { useMemo, useState } from 'react';
import { PORTAL_GROUPS, type Portal } from './data/portals';

function matches(portal: Portal, query: string): boolean {
  if (!query) return true;
  const haystack = `${portal.name} ${portal.description}`.toLowerCase();
  return haystack.includes(query.toLowerCase());
}

function PortalTile({ portal }: { portal: Portal }) {
  return (
    <div className="portal-tile">
      <h3 className="text-base font-semibold text-ink-900">{portal.name}</h3>
      <p className="flex-1 text-sm leading-relaxed text-slate-600">{portal.description}</p>
      <a href={portal.url} target="_blank" rel="noopener noreferrer" className="portal-link">
        Open portal
        <span aria-hidden="true">&rarr;</span>
      </a>
    </div>
  );
}

export function App() {
  const [query, setQuery] = useState('');

  const filteredGroups = useMemo(
    () =>
      PORTAL_GROUPS.map((group) => ({
        ...group,
        portals: group.portals.filter((portal) => matches(portal, query)),
      })),
    [query],
  );

  const totalMatches = filteredGroups.reduce((sum, g) => sum + g.portals.length, 0);
  const totalPortals = PORTAL_GROUPS.reduce((sum, g) => sum + g.portals.length, 0);

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="border-b border-slate-200 bg-ink-950 text-white">
        <div className="mx-auto max-w-6xl px-6 py-8">
          <p className="text-xs font-semibold uppercase tracking-widest text-brand-100/80">
            Republic of Seychelles
          </p>
          <h1 className="mt-1 text-2xl font-bold tracking-tight sm:text-3xl">
            One Gov Seychelles — Staff Portal Directory
          </h1>
          <p className="mt-2 max-w-3xl text-sm text-slate-300">
            A directory of every staff-facing portal console on the One Gov platform. Pick the one you
            need below — each portal has its own complete sign-in, so this page carries no session and
            no credentials of its own.
          </p>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-8">
        <div className="mb-8 max-w-md">
          <label htmlFor="portal-search" className="field-label">
            Search portals
          </label>
          <input
            id="portal-search"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter by name or coverage, e.g. &ldquo;passports&rdquo; or &ldquo;pensions&rdquo;"
            className="field-input"
          />
          <p className="mt-1.5 text-xs text-slate-500">
            {totalMatches} of {totalPortals} portals shown
          </p>
        </div>

        {filteredGroups.map((group) =>
          group.portals.length === 0 ? null : (
            <section key={group.title} className="mb-10">
              <h2 className="text-lg font-bold text-ink-900">{group.title}</h2>
              <p className="mt-0.5 text-sm text-slate-500">{group.blurb}</p>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {group.portals.map((portal) => (
                  <PortalTile key={portal.url} portal={portal} />
                ))}
              </div>
            </section>
          ),
        )}

        {totalMatches === 0 && (
          <p className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
            No portals match &ldquo;{query}&rdquo;.
          </p>
        )}
      </main>

      <footer className="mx-auto max-w-6xl px-6 pb-8 text-xs text-slate-400">
        This page only links to portals — sign-in, roles, and data access are all handled by each
        destination portal itself.
      </footer>
    </div>
  );
}
